import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { MdDownload } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import { CommonTable, Spinner } from "../../../Components/Common";
import {
  addProjectDocuments,
  deleteProjectDocuments,
  downloadProjectDocuments,
  getProjectDocuments,
} from "../../../Services/projectServices";
import { DOCUMENT_CATEGORIES, TOAST_MESSAGES } from "../constants";
import { toProperCase } from "../../../Utils";
import { downloadUtil } from "../../../Utils/DownloadUtil";

const RESET_PAGINATION = { offset: 0, limit: 5, count: 0 };

const ProjectDocuments = ({
  onClose,
  showDocumentsTable,
  showDocumentUpload,
  showProjectDocuments,
  setShowProjectDocuments,
  showHandover,
  project_Id,
  currentMilestoneStatus,
}) => {
  const [fileData, setFileData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [fileUploadLoading, setUploadLoading] = useState(false);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const [selectedCategory, setSelectedCategory] = useState(""); // required
  const [handoverToClient, setHandoverToClient] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const { projectId } = useParams();

  const fetchFileData = async (
    offset = 0,
    limit = RESET_PAGINATION.limit,
    handover_to_client = "",
    isInitial = false
  ) => {
    if (isInitial) setLoading(true);
    else setPaginationLoading(true);

    const current_project_id = projectId || project_Id;

    try {
      const response = await getProjectDocuments(current_project_id, {
        offset,
        limit,
        handover_to_client: handover_to_client,
      });
      if (response.status === 200) {
        const newFiles = response.data.results || [];
        setFileData((prev) => (isInitial ? newFiles : [...prev, ...newFiles]));
        setPagination({
          offset: offset + limit,
          limit: RESET_PAGINATION.limit,
          count: response.data.count || 0,
        });
      } else {
        toast.error(TOAST_MESSAGES.FILES_FETCHED_ERROR);
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.FILES_FETCHED_ERROR);
    } finally {
      if (isInitial) setLoading(false);
      else setPaginationLoading(false);
    }
  };

  useEffect(() => {
    if (showHandover) {
      fetchFileData(0, RESET_PAGINATION.limit, true, true);
    } else {
      fetchFileData(0, RESET_PAGINATION.limit, "", true);
    }
  }, []);

  const loadMoreFiles = () => {
    if (pagination.offset >= pagination.count) return;
    fetchFileData(pagination.offset, RESET_PAGINATION.limit);
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !selectedCategory) return;

    setUploadLoading(true);

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("category", selectedCategory);
    formData.append("handover_to_client", handoverToClient);

    const current_project_id = projectId || project_Id;
    try {
      const response = await addProjectDocuments(current_project_id, formData);
      if (response.status === 201) {
        toast.success(TOAST_MESSAGES.FILE_UPLOADED_SUCCESFULLY);
        setShowProjectDocuments(false);
        setSelectedCategory("");
        setHandoverToClient(false);
        setSelectedFile(null);
      } else {
        toast.error(TOAST_MESSAGES.FILE_UPLOAD_FAILED);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          TOAST_MESSAGES.FILE_UPLOAD_FAILED
      );
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteFile = async (fid) => {
    setLoading(true);
    const current_project_id = projectId || project_Id;
    try {
      const results = await deleteProjectDocuments(current_project_id, fid);
      if (results.status === 200) {
        toast.success(TOAST_MESSAGES.FILE_DELETE_SUCCESS);
        fetchFileData(0, RESET_PAGINATION.limit, true, true);
      } else {
        toast.error(TOAST_MESSAGES.FILE_DELETE_FAIL);
      }
    } catch {
      toast.error(TOAST_MESSAGES.FILE_DELETE_FAIL);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileUrl) => {
    try {
      await downloadUtil(fileUrl);
      toast.success(TOAST_MESSAGES.FILE_DOWNLOAD_SUCCESSFUL);
    } catch (error) {
      toast.error(TOAST_MESSAGES.FILE_DOWNLOAD_FAIL);
    }
  };

  const handleRenderField = (key, rowData) => {
    if (key === "Actions") {
      return (
        <div key={rowData.id} className="flex space-x-2">
          <button
            onClick={() => handleDownload(rowData.file)}
            className="flex items-center gap-1 px-3 py-1 text-secondary hover:text-blue-600"
            disabled={fileData.length === 0}
          >
            <MdDownload size={20} />
          </button>
          <button
            onClick={() => handleDeleteFile(rowData.id)}
            disabled={currentMilestoneStatus === "COMPLETED"}
            title={`${
              currentMilestoneStatus === "COMPLETED"
                ? "Can not delete, already completed"
                : "Delete file"
            }`}
            className={`${
              currentMilestoneStatus === "COMPLETED"
                ? "text-gray-400 cursor-not-allowed"
                : "text-red-500 hover:text-blue-600"
            }`}
          >
            <FiTrash2 size={20} />
          </button>
        </div>
      );
    }

    if (key === "File") {
      const fileName = rowData.file?.split("/").pop().split("?")[0];
      console.log("FIlename =>", fileName);
      return (
        <a
          href={rowData.file}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
          title={fileName}
        >
          {fileName || "N/A"}
        </a>
      );
    }

    if (key === "Category") {
      return toProperCase(rowData.category) || "N/A";
    }
    if (key === "Handover") {
      return rowData.handover_to_client ? "Yes" : "No";
    }

    return rowData[key] || "N/A";
  };

  const handleDownloadZip = async () => {
    showHandover
      ? downloadProjectDocuments(projectId || project_Id, {
          handover_to_client: true,
        })
      : downloadProjectDocuments(projectId || project_Id);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white max-w-[800px] max-h-[65vh] md:max-h-[75vh] rounded-md shadow-md mt-8">
        <div className="flex justify-between items-center m-4">
          <h2 className="text-md font-semibold text-center">
            Project Documents
          </h2>

          <div className="flex items-center gap-2">
            {showDocumentsTable && (
              <button
                onClick={handleDownloadZip}
                className="flex items-center gap-1 px-3 py-1 text-secondary hover:text-blue-600"
                disabled={fileData.length === 0}
              >
                <MdDownload size={20} />
              </button>
            )}

            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 text-xl"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Upload Form Section */}
        {showDocumentUpload && (
          <div className="w-full flex flex-col gap-4 bg-white p-6 rounded-md border">
            {/* Category Dropdown */}
            <div className="flex w-full justify-between items-center gap-3">
              <label className="text-sm text-gray-600 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-[70%] border rounded-lg px-3 py-2 text-gray-700 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                required
              >
                <option value="">Choose category</option>
                {DOCUMENT_CATEGORIES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            {/* File Choose */}
            <div className="flex w-full justify-between items-center">
              <label className="text-sm text-gray-600 mb-1">
                Choose File <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="w-[70%] text-sm text-gray-700 border border-gray-300 rounded-lg cursor-pointer bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            {/* Handover Checkbox */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={handoverToClient}
                onChange={(e) => setHandoverToClient(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-700 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Handover to Client</span>
            </div>

            {/* Upload Button */}
            <button
              onClick={handleFileUpload}
              disabled={fileUploadLoading || !selectedFile || !selectedCategory}
              className="mt-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {fileUploadLoading ? "Uploading..." : "Upload File"}
            </button>
          </div>
        )}

        {/* File list with infinite scroll */}
        {showDocumentsTable && (
          <div
            id="projectFileScrollDiv"
            className="max-h-[55vh] md:max-h-[60vh] overflow-x-hidden overflow-y-scroll thin-scrollbar p-4"
          >
            {loading ? (
              <div className="flex justify-center items-center">
                <Spinner />
              </div>
            ) : (
              <InfiniteScroll
                dataLength={fileData.length}
                next={loadMoreFiles}
                hasMore={pagination.offset < pagination.count}
                scrollableTarget="projectFileScrollDiv"
                loader={
                  paginationLoading && (
                    <div className="flex justify-center py-2">
                      <Spinner size={3} />
                    </div>
                  )
                }
              >
                <CommonTable
                  tableColumns={["File", "Category", "Handover", "Actions"]}
                  tableRows={fileData}
                  renderField={handleRenderField}
                  className="border-2 shadow-md text-md"
                />
              </InfiniteScroll>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDocuments;
